import { defineStore } from 'pinia'
import { stripInvalidProperties } from '@/utils/helpers'
import { makeResetStoreAction, createPersistedState, VERSION_KEY } from '@/utils/helpers/pinia'

const VERSION = 2

export const state = () => ({
  [VERSION_KEY]: VERSION,
  sidebar: {
    visible: true,
    collapsed: false,
  },
  isFullScreen: false,
  // True while a logout tears the session down: the page is hidden so it does not render the
  // stores LOGOUT resets
  isLoggingOut: false,
  header: null,
  dataTables: {},
  dataTableLastOrgCanonical: {},
  _counter: 0,
})

function makeGetDataTableFilters (state, _counter) {
  return (pageName) => state.dataTables?.[pageName]?.filters ?? {}
}

function makeGetDataTableProps (state, _counter) {
  return (pageName) => state.dataTables?.[pageName] ?? {}
}

export function normalizeSortOptions (items) {
  if (Array.isArray(items)) {
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (typeof item === 'string') {
        items[i] = { key: item, order: 'desc' }
      }
    }
  }
}

export const useLayoutStore = defineStore('layout', {
  state,

  getters: {
    getDataTableProps: (state) => makeGetDataTableProps(state, state._counter),
    getDataTableFilters: (state) => makeGetDataTableFilters(state, state._counter),
    getDataTableLastOrgCanonical: (state) => (name) => state.dataTableLastOrgCanonical?.[name],
  },

  actions: {
    /**
     * Reset the layout state for a specific key or all state
     */
    RESET_LAYOUT_STATE: makeResetStoreAction(state),

    /**
     * Toggle sidebar visibility
     */
    TOGGLE_SIDEBAR_VISIBILITY () {
      this.sidebar.visible = !this.sidebar.visible
    },

    /**
     * Toggle sidebar collapsed mode
     */
    TOGGLE_SIDEBAR_MODE () {
      this.sidebar.collapsed = !this.sidebar.collapsed
    },

    /**
     * Hide or show the page while a logout tears the session down
     * @param {boolean} isLoggingOut
     */
    SET_LOGGING_OUT (isLoggingOut) {
      this.isLoggingOut = isLoggingOut
    },

    /**
     * Toggle full screen mode
     */
    TOGGLE_FULL_SCREEN () {
      this.isFullScreen = !this.isFullScreen
    },

    /**
     * Update filters time range
     * time-range-filter.vue subscribes to this to update the date range
     * it's needed to have the values reset to count from "now"
     * @param {{ name: string, time: string }} payload
     */
    UPDATE_FILTERS_TIME_RANGE ({ name, time }) {
      this.dataTables[name].filters = { ...this.dataTables[name]?.filters, end: time }
      // FE-1053: increment counter to trigger update on computed
      this._counter++
    },

    /**
     * Reset data table search term
     * @param {{ name: string }} payload
     */
    RESET_DATA_TABLE_SEARCH ({ name }) {
      this.dataTables[name].searchTerm = ''
      // FE-1053: increment counter to trigger update on computed
      this._counter++
    },

    /**
     * Reset data table page to 1
     * @param {{ name: string }} payload
     */
    RESET_DATA_TABLE_PAGE ({ name }) {
      if (this.dataTables[name]) {
        this.dataTables[name].page = 1
      }
      // FE-1053: increment counter to trigger update on computed
      this._counter++
    },

    /**
     * Store last org canonical per table name (used to detect org change and reset page across component instances)
     * @param {{ name: string, orgCanonical: string|null }} payload
     */
    SET_DATA_TABLE_LAST_ORG_CANONICAL ({ name, orgCanonical }) {
      this.dataTableLastOrgCanonical[name] = orgCanonical
    },

    /**
     * Reset data table filters
     * @param {{ name: string }} payload
     */
    RESET_DATA_TABLE_FILTERS ({ name }) {
      this.dataTables[name].filters = {}
      // FE-1053: increment counter to trigger update on computed
      this._counter++
    },

    /**
     * Set data table properties
     * @param {{ name: string, props: object }} payload
     */
    SET_DATA_TABLE_PROPS ({ name, props }) {
      this.dataTables[name] = props
      // FE-1053: increment counter to trigger update on computed
      this._counter++
    },

    /**
     * Set data table filters
     * @param {{ name: string, filters: object }} payload
     */
    SET_DATA_TABLE_FILTERS ({ name, filters }) {
      const processedFilters = stripInvalidProperties(filters)
      const dataTableProps = {
        ...this.dataTables[name],
        filters: _.$isEmpty(processedFilters) ? null : processedFilters,
      }
      this.dataTables[name] = dataTableProps
      // FE-1053: increment counter to trigger update on computed
      this._counter++
    },

    /**
     * Set header content
     * @param {any} headerContent
     */
    SET_HEADER (headerContent) {
      this.header = headerContent
    },

    /**
     * Remove data table filter
     * @param {{ name: string, filterType: string, filterValue: string }} payload
     */
    REMOVE_DATA_TABLE_FILTER ({ name, filterType, filterValue }) {
      const filters = _.cloneDeep(this.dataTables[name].filters)

      if (filterType.includes('[in]')) {
        const filteredList = _.$get(this.dataTables[name].filters, filterType, '')
          .split(',')
          .filter((id) => id !== filterValue)
          .toString()

        if (_.$isEmpty(filteredList)) {
          delete filters[filterType]
        } else {
          filters[filterType] = filteredList
        }
      } else {
        delete filters[filterType]
      }

      this.SET_DATA_TABLE_FILTERS({ name, filters })
    },
  },
  persistedState: createPersistedState('cyLayout', {
    version: VERSION,
    includePaths: ['sidebar.collapsed', 'dataTables'],
    migrations: {
      2: (state) => {
        for (const pageName of Object.keys(state.dataTables)) {
          const dataTable = state.dataTables[pageName]
          normalizeSortOptions(dataTable.sortBy)
          normalizeSortOptions(dataTable.groupBy)
        }
        return state
      },
    },
  }),
})
